// offline_db.js — IndexedDB wrapper for Taagger offline storage
// Stores: tasks, assets, locations, calendar_events, sync_queue, conflicts, meta,
//         file_queue, parts, custody_events, signatures,
//         compliance_documents, pending_doc_signatures,
//         members, checklist_templates

import { openDB } from 'idb';

const DB_NAME    = 'taagger-offline';
const DB_VERSION = 9;

let _db = null;

export async function getDB() {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, _newVersion, transaction) {
      const ensureStore = (name, options) => (
        db.objectStoreNames.contains(name)
          ? transaction.objectStore(name)
          : db.createObjectStore(name, options)
      );
      const ensureIndex = (store, name, keyPath, options = {}) => {
        if (!store.indexNames.contains(name)) store.createIndex(name, keyPath, options);
      };

      const ts = ensureStore('tasks', { keyPath: 'id' });
      ensureIndex(ts, 'by_status', 'status');
      ensureIndex(ts, 'by_updated', 'updated_at');

      const as = ensureStore('assets', { keyPath: 'id' });
      ensureIndex(as, 'by_status', 'status');

      ensureStore('locations', { keyPath: 'id' });
      ensureStore('sync_queue', { keyPath: '_qid', autoIncrement: true });
      ensureStore('meta', { keyPath: 'key' });

      const ce = ensureStore('calendar_events', { keyPath: 'id' });
      ensureIndex(ce, 'by_due_date', 'due_date');

      const cf = ensureStore('conflicts', { keyPath: '_cid', autoIncrement: true });
      ensureIndex(cf, 'by_entity', ['entity_type', 'entity_id']);
      ensureIndex(cf, 'by_resolved', 'resolved');

      const fq = ensureStore('file_queue', { keyPath: '_fqid', autoIncrement: true });
      ensureIndex(fq, 'by_entity', ['entity_type', 'entity_id']);
      ensureIndex(fq, 'by_status', 'status');

      const ps = ensureStore('parts', { keyPath: 'id' });
      ensureIndex(ps, 'by_status', 'status');

      ensureStore('custody_events', { keyPath: '_ceid', autoIncrement: true });

      const sg = ensureStore('signatures', { keyPath: '_sid', autoIncrement: true });
      ensureIndex(sg, 'by_task', 'task_id');
      ensureIndex(sg, 'by_synced', 'synced');

      const cd = ensureStore('compliance_documents', { keyPath: 'id' });
      ensureIndex(cd, 'by_asset', 'asset_id');
      ensureIndex(cd, 'by_company', 'company_id');

      const pd = ensureStore('pending_doc_signatures', { keyPath: 'id' });
      ensureIndex(pd, 'by_doc', 'compliance_document_id');
      ensureIndex(pd, 'by_status', 'status');

      const mb = ensureStore('members', { keyPath: 'id' });
      ensureIndex(mb, 'by_name', 'name');

      const ct = ensureStore('checklist_templates', { keyPath: 'id' });
      ensureIndex(ct, 'by_category', 'category');

      const cm = ensureStore('comments', { keyPath: '_cmid', autoIncrement: true });
      ensureIndex(cm, 'by_task', 'task_id');
      ensureIndex(cm, 'by_server_id', 'id');
    }
  });
  return _db;
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

export async function storeTasks(tasks) {
  const db = await getDB();
  const tx = db.transaction('tasks', 'readwrite');
  await Promise.all([...tasks.map(t => tx.store.put(t)), tx.done]);
}

export async function getAllTasks() {
  const db = await getDB();
  return db.getAll('tasks');
}

export async function getTask(id) {
  const db = await getDB();
  return db.get('tasks', id);
}

export async function removeTask(id) {
  const db = await getDB();
  return db.delete('tasks', id);
}

export async function updateTaskLocal(id, changes) {
  const db = await getDB();
  const task = await db.get('tasks', id);
  if (task) await db.put('tasks', { ...task, ...changes, updated_at: new Date().toISOString() });
}

// ── Assets ────────────────────────────────────────────────────────────────────

export async function storeAssets(assets) {
  const db = await getDB();
  const tx = db.transaction('assets', 'readwrite');
  await Promise.all([...assets.map(a => tx.store.put(a)), tx.done]);
}

export async function getAllAssets() {
  const db = await getDB();
  return db.getAll('assets');
}

// ── Locations ─────────────────────────────────────────────────────────────────

export async function storeLocations(locations) {
  const db = await getDB();
  const tx = db.transaction('locations', 'readwrite');
  await Promise.all([...locations.map(l => tx.store.put(l)), tx.done]);
}

export async function getAllLocations() {
  const db = await getDB();
  return db.getAll('locations');
}

// ── Calendar events ───────────────────────────────────────────────────────────

export async function storeCalendarEvents(events) {
  const db = await getDB();
  const tx = db.transaction('calendar_events', 'readwrite');
  await Promise.all([...events.map(e => tx.store.put(e)), tx.done]);
}

export async function getCalendarEvents(fromDate, toDate) {
  const db     = await getDB();
  const all    = await db.getAll('calendar_events');
  const from   = fromDate ? new Date(fromDate) : null;
  const to     = toDate   ? new Date(toDate)   : null;
  return all.filter(e => {
    if (!e.due_date) return false;
    const d = new Date(e.due_date);
    if (from && d < from) return false;
    if (to   && d > to)   return false;
    return true;
  });
}

// ── Sync queue ────────────────────────────────────────────────────────────────

export async function enqueue(entry) {
  const db = await getDB();
  return db.add('sync_queue', {
    ...entry,
    queued_at: entry.queued_at || new Date().toISOString(),
    retries:   0
  });
}

export async function getPendingQueue() {
  const db = await getDB();
  return db.getAll('sync_queue');
}

export async function removeFromQueue(qid) {
  const db = await getDB();
  return db.delete('sync_queue', qid);
}

export async function clearQueue() {
  const db = await getDB();
  return db.clear('sync_queue');
}

export async function getPendingCount() {
  const db = await getDB();
  return db.count('sync_queue');
}

// ── Meta (last sync time, company id, etc.) ───────────────────────────────────

export async function setMeta(key, value) {
  const db = await getDB();
  return db.put('meta', { key, value });
}

export async function getMeta(key) {
  const db = await getDB();
  const row = await db.get('meta', key);
  return row?.value;
}

// ── Counts ────────────────────────────────────────────────────────────────────

export async function getStoreCounts() {
  const db = await getDB();
  const [tasks, assets, queue, calendar, conflicts, parts, docs, pendingSigs, members, checklistTemplates] = await Promise.all([
    db.count('tasks'),
    db.count('assets'),
    db.count('sync_queue'),
    db.count('calendar_events'),
    db.getAll('conflicts').then(all => all.filter(c => !c.resolved).length),
    db.count('parts').catch(() => 0),
    db.count('compliance_documents').catch(() => 0),
    db.count('pending_doc_signatures').catch(() => 0),
    db.count('members').catch(() => 0),
    db.count('checklist_templates').catch(() => 0)
  ]);
  return { tasks, assets, queue, calendar, conflicts, parts, docs, pendingSigs, members, checklistTemplates };
}

// ── Conflicts ─────────────────────────────────────────────────────────────────

export async function storeConflict(conflict) {
  const db = await getDB();
  return db.add('conflicts', {
    ...conflict,
    resolved: false,
    created_at: new Date().toISOString()
  });
}

export async function getConflicts() {
  const db  = await getDB();
  const all = await db.getAll('conflicts');
  return all.filter(c => !c.resolved);
}

export async function markConflictResolved(cid) {
  const db       = await getDB();
  const conflict = await db.get('conflicts', cid);
  if (conflict) await db.put('conflicts', { ...conflict, resolved: true });
}

export async function getConflictCount() {
  const db  = await getDB();
  const all = await db.getAll('conflicts');
  return all.filter(c => !c.resolved).length;
}

// ── File upload queue ─────────────────────────────────────────────────────────
// Files selected while offline are stored as ArrayBuffers here.
// On reconnect, they are DirectUploaded to S3 and attached to their record.

export async function enqueueFile(entry) {
  const db = await getDB();
  return db.add('file_queue', {
    ...entry,
    status:    'pending',
    queued_at: new Date().toISOString(),
    retries:   0
  });
}

export async function getPendingFiles() {
  const db  = await getDB();
  const all = await db.getAll('file_queue');
  return all.filter(f => f.status === 'pending' || f.status === 'failed');
}

export async function getAllFileQueue() {
  const db = await getDB();
  return db.getAll('file_queue');
}

export async function updateFileQueueEntry(fqid, changes) {
  const db    = await getDB();
  const entry = await db.get('file_queue', fqid);
  if (entry) await db.put('file_queue', { ...entry, ...changes });
}

export async function removeFromFileQueue(fqid) {
  const db = await getDB();
  return db.delete('file_queue', fqid);
}

export async function getPendingFileCount() {
  const db  = await getDB();
  const all = await db.getAll('file_queue');
  return all.filter(f => f.status === 'pending').length;
}

// ── Parts (read-only offline) ─────────────────────────────────────────────────

export async function storeParts(parts) {
  const db = await getDB();
  const tx = db.transaction('parts', 'readwrite');
  await Promise.all([...parts.map(p => tx.store.put(p)), tx.done]);
}

export async function getAllParts() {
  const db = await getDB();
  return db.getAll('parts').catch(() => []);
}

// ── Custody events (check-in/out, offline queue) ──────────────────────────────

export async function enqueueCustodyEvent(entry) {
  const db = await getDB();
  return db.add('custody_events', {
    ...entry,
    synced:     false,
    queued_at:  new Date().toISOString()
  }).catch(() => null);
}

export async function getPendingCustodyEvents() {
  const db  = await getDB();
  const all = await db.getAll('custody_events').catch(() => []);
  return all.filter(e => !e.synced);
}

export async function markCustodyEventSynced(ceid) {
  const db    = await getDB();
  const entry = await db.get('custody_events', ceid);
  if (entry) await db.put('custody_events', { ...entry, synced: true });
}

// ── Signatures (offline sign-off, uploaded on reconnect) ─────────────────────

export async function storeSignature(entry) {
  const db = await getDB();
  return db.add('signatures', {
    ...entry,
    synced:    false,
    signed_at: new Date().toISOString()
  }).catch(() => null);
}

export async function getPendingSignatures() {
  const db  = await getDB();
  const all = await db.getAll('signatures').catch(() => []);
  return all.filter(s => !s.synced);
}

export async function markSignatureSynced(sid) {
  const db  = await getDB();
  const sig = await db.get('signatures', sid);
  if (sig) await db.put('signatures', { ...sig, synced: true });
}

// ── Compliance documents (synced for offline signing) ────────────────────────

export async function storeComplianceDocs(docs) {
  const db = await getDB();
  const tx = db.transaction('compliance_documents', 'readwrite');
  await Promise.all([...docs.map(d => tx.store.put(d)), tx.done]);
}

export async function getAllComplianceDocs() {
  const db = await getDB();
  return db.getAll('compliance_documents').catch(() => []);
}

// ── Pending document signatures (per-user, synced for offline signing) ────────

export async function storePendingDocSignatures(sigs) {
  const db = await getDB();
  const tx = db.transaction('pending_doc_signatures', 'readwrite');
  await Promise.all([...sigs.map(s => tx.store.put(s)), tx.done]);
}

export async function getAllPendingDocSignatures() {
  const db = await getDB();
  return db.getAll('pending_doc_signatures').catch(() => []);
}

export async function getOfflineDocSignatures() {
  const db  = await getDB();
  const all = await db.getAll('pending_doc_signatures').catch(() => []);
  return all.filter(s => s._offline_signed && !s._offline_synced);
}

export async function markDocSignatureSynced(id) {
  const db  = await getDB();
  const sig = await db.get('pending_doc_signatures', id);
  if (sig) await db.put('pending_doc_signatures', { ...sig, _offline_synced: true });
}

// ── Members (synced for offline reference) ────────────────────────────────────

export async function storeMembers(members) {
  const db = await getDB();
  const tx = db.transaction('members', 'readwrite');
  await Promise.all([...members.map(m => tx.store.put(m)), tx.done]);
}

export async function getMembers() {
  const db = await getDB();
  return db.getAll('members').catch(() => []);
}

// ── Checklist templates (synced for offline browse) ───────────────────────────

export async function storeChecklistTemplates(templates) {
  const db = await getDB();
  const tx = db.transaction('checklist_templates', 'readwrite');
  await Promise.all([...templates.map(t => tx.store.put(t)), tx.done]);
}

export async function getChecklistTemplates() {
  const db = await getDB();
  return db.getAll('checklist_templates').catch(() => []);
}

// ── Comments (synced per task for offline viewing) ─────────────────────────────

export async function storeComments(comments) {
  const db = await getDB();
  const tx = db.transaction('comments', 'readwrite');
  const normalized = comments.map(c => ({
    ...c,
    _cmid: c._cmid || (c.id ? `server-${c.id}` : undefined)
  }));
  await Promise.all([...normalized.map(c => tx.store.put(c)), tx.done]);
}

export async function getCommentsByTask(taskId) {
  const db  = await getDB();
  const all = await db.getAllFromIndex('comments', 'by_task', taskId).catch(() => []);
  return all.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
}

export async function addComment(comment) {
  const db = await getDB();
  return db.add('comments', comment);
}
